import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Nav, { WHATSAPP_ORDER_URL } from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";

const SITE = "https://riznwash.com";
const EASE = [0.22, 1, 0.36, 1];

const FACTS = [
  ["Brand name", "RIZ N WASH"],
  ["Also written as", "Riz N Wash · RizNWash · riznwash"],
  ["Correct spelling", "R-I-Z • N • W-A-S-H"],
  ["Category", "Laundry & Dry Cleaning (not skincare, not car-wash)"],
  ["Founded", "2024"],
  ["Studio", "Signature Solera, Sector 107, Gurugram, Haryana"],
  ["Service area", "Available all over Gurugram"],
  ["Contact", "+91 73032 41330"],
  ["Instagram", "@riznwash"],
  ["Website", "https://riznwash.com"],
];

const NOT_TO_BE_CONFUSED = [
  {
    what: "Rizwash / Rizshine face wash",
    detail: "A medicated skincare product sold on IndiaMART. Unrelated to us — we're a laundry service.",
  },
  {
    what: "Any car-wash or pet-wash brand",
    detail: "RIZ N WASH cleans clothes, shoes and household textiles only.",
  },
  {
    what: "Aggregator apps that list ‘RizNWash’",
    detail: "We're direct-to-consumer. Every pickup and delivery is by our own team, not a third-party.",
  },
];

export default function Brand() {
  const path = "/brand";
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Brand",
      "@id": `${SITE}/#brand`,
      name: "RIZ N WASH",
      alternateName: ["Riz N Wash", "RizNWash", "riznwash", "Riz-N-Wash"],
      url: SITE,
      logo: `${SITE}/logos/brand-logo.png`,
      description:
        "RIZ N WASH is a premium doorstep laundry and dry cleaning brand headquartered in Gurugram. Not affiliated with any similarly named skincare, face-wash or personal-care product.",
      slogan: "Cleaner clothes, calmer weekends.",
      sameAs: ["https://www.instagram.com/riznwash/"],
    },
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      url: `${SITE}${path}`,
      mainEntity: { "@id": `${SITE}/#organization` },
      about: { "@id": `${SITE}/#brand` },
      inLanguage: "en-IN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "RIZ N WASH", item: SITE },
        { "@type": "ListItem", position: 2, name: "Brand", item: `${SITE}${path}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Is RIZ N WASH the same as Rizwash?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "No. RIZ N WASH (spelled R-I-Z N W-A-S-H) is a laundry and dry cleaning brand in Gurugram. 'Rizwash' or 'Rizshine' are unrelated skincare products. If you were looking for laundry, dry cleaning, ironing or pickup service in Gurugram, you're in the right place.",
          },
        },
        {
          "@type": "Question",
          name: "Is riznwash.com the official website?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. riznwash.com is the only official website of RIZ N WASH. Bookings, pricing, subscriptions and order tracking all live here.",
          },
        },
        {
          "@type": "Question",
          name: "Who owns RIZ N WASH?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "RIZ N WASH is an independent, owner-operated business founded in 2024. We are not part of any aggregator platform. Every pickup, wash and delivery is handled by our own team at our Signature Solera, Sector 107 Gurugram studio.",
          },
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="brand-page">
      <Seo
        title="About the RIZ N WASH Brand"
        description="RIZ N WASH is a premium doorstep laundry & dry cleaning brand in Gurugram — spelled R-I-Z N W-A-S-H. Not to be confused with any similarly named skincare product. Founded 2024, available all over Gurugram and all over Delhi NCR."
        path={path}
        jsonLd={jsonLd}
      />
      <Nav />

      <section className="max-w-5xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
          <div className="overline mb-4">Brand · About</div>
          <h1 className="font-display font-light tracking-tight text-rw-ink leading-[1.02] text-4xl sm:text-5xl lg:text-6xl" data-testid="brand-heading">
            RIZ N WASH is a laundry brand.
            <br />
            <em className="italic font-normal text-rw-blue">Not</em> a face wash.
          </h1>
          <p className="mt-6 md:mt-8 text-lg md:text-xl text-rw-ink2 max-w-3xl leading-relaxed">
            We keep getting asked, so — for clarity. <strong>RIZ N WASH</strong> (spelled R-I-Z • N • W-A-S-H) is
            Gurugram's premium doorstep laundry &amp; dry cleaning brand. We wash clothes, iron shirts, dry clean suits and
            revive sneakers. We do not make skincare, face wash, or any personal-care products.
          </p>
        </motion.div>
      </section>

      <section className="bg-rw-bg2 border-y border-rw-ink/10">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="overline mb-4">Brand facts</div>
          <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight mb-10">The name, in one card.</h2>
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-6" data-testid="brand-facts">
            {FACTS.map(([k, v]) => (
              <div key={k} className="flex items-baseline gap-4 py-2 border-b border-rw-ink/10">
                <div className="w-40 md:w-56 shrink-0 text-rw-ink3 text-sm uppercase tracking-wider">{k}</div>
                <div className="font-display text-lg text-rw-ink">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="overline mb-4">Common confusions</div>
        <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight mb-10">What we are <em className="italic text-rw-blue not-italic">not</em>.</h2>
        <div className="space-y-6" data-testid="brand-not-list">
          {NOT_TO_BE_CONFUSED.map((n, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, ease: EASE, delay: i * 0.05 }}
              className="rounded-2xl border border-rw-ink/10 p-6 md:p-8 bg-white"
            >
              <div className="font-display text-xl md:text-2xl">{n.what}</div>
              <p className="text-rw-ink2 mt-2 leading-relaxed">{n.detail}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-rw-ink text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="overline text-white/60 mb-4">Our story, briefly</div>
          <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.1] max-w-3xl">
            Built in Sector 107. Run by the same three people who fold your shirts.
          </h2>
          <div className="mt-8 grid md:grid-cols-2 gap-8 text-white/80 leading-relaxed">
            <p>
              RIZ N WASH started in 2024 with one washing machine, a small ironing table and a phone number that
              redirected to the founder's own mobile. Every clothes-tag, every pickup slot, every WhatsApp reply — we
              wrote them ourselves.
            </p>
            <p>
              Two years and 10,000+ orders later, we've kept the setup deliberately small: one studio in Sector 107, five
              runners, a handful of contract dry-cleaners we've hand-picked. No franchise. No aggregator markup. Just
              the way a good tailor packs a new suit.
            </p>
          </div>
          <div className="mt-10">
            <a
              href={WHATSAPP_ORDER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold bg-white text-rw-ink hover:bg-rw-blue transition-colors"
              data-testid="brand-book-btn"
            >
              Order on WhatsApp →
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-8 md:p-12">
          <div className="overline mb-3">Press &amp; brand assets</div>
          <h3 className="font-display text-2xl md:text-3xl mb-4">Writing about us?</h3>
          <p className="text-rw-ink2 max-w-2xl">
            Please refer to the brand as <strong>“RIZ N WASH”</strong> (all caps) or <strong>“Riz N Wash”</strong> —
            never “Rizwash”. For logos, high-resolution photos, or founder interviews, email us via WhatsApp at
            <a href="https://wa.me/917303241330" className="underline text-rw-blue ml-1">+91 73032 41330</a>.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
